<template>
  <picture class="base-image" :style="baseStyle">
    <source v-if="props.srcSet" :srcset="props.srcSet" :sizes="props.sizes">
    <div
      class="base-image__skeleton"
      :class="{ 'base-image__skeleton--hidden': isLoaded }"
    />
    <div
      class="base-image__loader"
      :class="{ 'base-image__loader--hidden': isLoaded }"
    >
      <BaseLoader
        size="40px"
        color="var(--color-foreground-caption-300)"
      />
    </div>
    <img
      ref="imgRef"
      v-bind="imgAttrs"
      :src="currentSrc"
      :sizes="props.sizes"
      :alt="props.alt"
      class="base-image__img"
      :class="{ 'base-image__img--loaded': isLoaded }"
      :style="imgStyle"
      @load="handleLoad"
      @error="handleError"
    >
  </picture>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import BaseLoader from '../loader/index.vue'

const props = withDefaults(defineProps<{
  src: string
  alt?: string
  srcSet?: string
  sizes?: string
  width?: number | string
  height?: number | string
  maxHeight?: number | string
  maxWidth?: number | string
  radius?: string
  fallbackSrc?: string
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  objectPosition?: 'bottom' | 'center' | 'top' | 'left' | 'right'
  withCrossOrigin?: boolean
}>(), {
  radius: '0',
  withCrossOrigin: false,
})

const isLoaded = ref(false)
const currentSrc = ref(props.src)
const imgRef = ref<HTMLImageElement | null>(null)

watch(
  () => props.src,
  (value: string) => {
    currentSrc.value = value
    isLoaded.value = false
  },
)

const baseStyle = computed(() => ({
  width: props.width ? `${props.width}px` : '100%',
  height: props.height ? `${props.height}px` : '100%',
  maxWidth: props.maxWidth ? `${props.maxWidth}` : '100%',
  maxHeight: props.maxHeight ? `${props.maxHeight}` : '100%',
}))

const imgStyle = computed(() => ({
  objectFit: props.objectFit ?? 'cover',
  objectPosition: props.objectPosition,
}))

const imgAttrs = computed(() => {
  const attrs: Record<string, string | number | undefined> = {
    width: props.width || undefined,
    height: props.height || undefined,
    loading: 'lazy',
    decoding: 'async',
    referrerpolicy: 'no-referrer',
  }

  if (props.withCrossOrigin) {
    attrs.crossorigin = 'anonymous'
  }

  return attrs
})

function handleLoad() {
  isLoaded.value = true
}

function handleError() {
  if (props.fallbackSrc && currentSrc.value !== props.fallbackSrc) {
    currentSrc.value = props.fallbackSrc
  }
  else {
    isLoaded.value = true
  }
}

onMounted(() => {
  if (imgRef.value?.complete) {
    handleLoad()
  }
})
</script>

<style lang="scss" scoped>
.base-image {
  position: relative;
  z-index: 0;
  height: 100%;
  display: flex;

  &__skeleton {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.2s infinite linear;
    transition: opacity 0.3s ease-in-out;

    &--hidden {
      opacity: 0;
      visibility: hidden;
    }
  }

  &__loader {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;

    &--hidden {
      opacity: 0;
      visibility: hidden;
    }
  }

  &__img {
    position: relative;
    z-index: 2;
    width: 100%;
    height: auto;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    border-radius: v-bind(radius);

    &--loaded {
      opacity: 1;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}
</style>
